import { FC } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faExclamation } from '@fortawesome/pro-solid-svg-icons/faExclamation';
import { faInfo } from '@fortawesome/pro-solid-svg-icons/faInfo';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { cn, FaIcon, TStatus, statuses } from '@fe-learner-common';

type StatusWithoutIcon = Exclude<TStatus, 'unknown'>;

type StatusIconProps = {
  className?: string;
  status: TStatus;
};

type IconStyle = {
  default: string;
  borderedIcon: string;
};

const isStatusWithIcon = (status: TStatus): status is StatusWithoutIcon => {
  return statuses.includes(status);
};

const colorMap: Record<TStatus, IconStyle> = {
  info: {
    default: 'lf:bg-prominence-500',
    borderedIcon: 'lf:border-prominence-50',
  },
  warning: {
    default: 'lf:bg-attention-500',
    borderedIcon: 'lf:border-attention-50',
  },
  positive: {
    default: 'lf:bg-positive-500',
    borderedIcon: 'lf:border-positive-50',
  },
  negative: {
    default: 'lf:bg-negative-500',
    borderedIcon: 'lf:border-negative-50',
  },
  unknown: {
    default: 'lf:bg-neutral-50 lf:border-neutral-400 lf:border-2',
    borderedIcon: 'lf:border-neutral-50 ',
  },
};

const iconMap: Record<StatusWithoutIcon, IconDefinition> = {
  info: faInfo,
  warning: faExclamation,
  positive: faCheck,
  negative: faXmark,
};

export const Icon: FC<StatusIconProps> = ({ status, className }) => {
  const icon = isStatusWithIcon(status) ? iconMap[status] : null;

  return (
    <div
      className={cn(
        'lf:box-content lf:flex lf:h-[20px] lf:w-[20px] lf:items-center lf:justify-center lf:gap-2 lf:rounded-full',
        colorMap[status].default,
        className,
      )}
    >
      {icon && (
        <FaIcon className="lf:text-neutral-0" width={12} height={12} icon={icon} />
      )}
    </div>
  );
};

export const BorderedStatusIcon: FC<StatusIconProps> = ({ status }) => (
  <Icon
    status={status}
    className={cn('lf:h-[24px] lf:w-[24px] lf:border-12', colorMap[status].borderedIcon)}
  />
);
